import { useEffect, useRef, useState } from "react";
import { Accessibility, X, RotateCcw } from "lucide-react";
import { useA11y } from "@/context/A11yContext";

const TOGGLES = [
    { key: "contrast", label: "Alto contrasto" },
    { key: "font", label: "Font leggibile" },
    { key: "spacing", label: "Spaziatura testo" },
    { key: "links", label: "Evidenzia link" },
    { key: "motion", label: "Riduci animazioni" },
];

function ToggleRow({ item, checked, onChange }) {
    const id = `a11y-${item.key}`;
    return (
        <div className="flex items-center justify-between gap-3 py-2">
            <span id={`${id}-label`} className="font-medium">{item.label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-labelledby={`${id}-label`}
                data-testid={`a11y-toggle-${item.key}`}
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-lg border-2 border-transparent ${checked ? "bg-edi-lime" : "bg-edi-track"}`}
            >
                <span className={`inline-block h-5 w-5 rounded-md bg-edi-surface border border-edi-border-strong ${checked ? "translate-x-5" : "translate-x-0.5"}`} style={{ transition: "transform .2s" }} />
                <span className="sr-only">{checked ? "attivo" : "non attivo"}</span>
            </button>
        </div>
    );
}

export const AccessibilityWidget = () => {
    const { settings, update, reset } = useA11y();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        panelRef.current?.querySelector("button")?.focus();
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);

    return (
        <div className="fixed bottom-4 left-4 z-[60] max-w-[calc(100vw-2rem)]">
            {open && (
                <div
                    ref={panelRef}
                    id="a11y-panel"
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby="a11y-panel-title"
                    data-testid="a11y-panel"
                    className="edi-card mb-3 w-[min(22rem,calc(100vw-2rem))] max-h-[75vh] overflow-y-auto p-5 shadow-md text-edi-text"
                >
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <h2 id="a11y-panel-title" className="text-lg font-bold">Accessibilità</h2>
                        <button type="button" className="btn-ghost" onClick={() => { setOpen(false); triggerRef.current?.focus(); }} data-testid="a11y-close-button">
                            <X aria-hidden="true" size={18} /> <span>Chiudi</span>
                        </button>
                    </div>
                    <fieldset className="mb-3">
                        <legend className="font-medium mb-2">Dimensione del testo</legend>
                        <div className="grid grid-cols-3 gap-2">
                            {[100, 125, 150].map((size) => (
                                <button
                                    key={size}
                                    type="button"
                                    aria-pressed={settings.textSize === size}
                                    data-testid={`a11y-text-size-${size}`}
                                    onClick={() => update({ textSize: size })}
                                    className={`rounded-md border-2 px-2 py-2 font-semibold ${settings.textSize === size ? "edi-active border-transparent" : "bg-edi-tertiary text-edi-text border-transparent"}`}
                                >
                                    {size}%
                                </button>
                            ))}
                        </div>
                    </fieldset>
                    <div className="divide-y divide-[var(--edi-border)]">
                        {TOGGLES.map((t) => (
                            <ToggleRow key={t.key} item={t} checked={Boolean(settings[t.key])} onChange={(v) => update({ [t.key]: v })} />
                        ))}
                    </div>
                    <button type="button" className="btn-secondary w-full mt-4" onClick={reset} data-testid="a11y-reset-button">
                        <RotateCcw aria-hidden="true" size={18} /> Ripristina impostazioni
                    </button>
                    <p className="edi-help mt-3">Le preferenze sono salvate su questo dispositivo.</p>
                </div>
            )}
            <button
                ref={triggerRef}
                type="button"
                aria-expanded={open}
                aria-controls="a11y-panel"
                data-testid="a11y-open-button"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-lg bg-edi-surface text-edi-text border border-edi-border px-4 py-3 font-semibold shadow-md"
            >
                <Accessibility aria-hidden="true" size={20} />
                Accessibilità
            </button>
        </div>
    );
};
